/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useServerForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing Telephony Server form state and input changes.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';
import { TelephonyServerFormData } from '../types';

const initialFormState: TelephonyServerFormData = {
  name: '',
  description: '',
  host: '',
  port: 22,
  username: '',
  password: '',
  eslHost: '',
  freeswitchPort: 8021,
  freeswitchPassword: 'ClueCon',
  timezone: 'UTC',
  location: '',
  monitoringEnabled: true,
  healthCheckInterval: 30,
  useVpn: false,
  bindInterface: 'auto',
  vpnIp: '',
};

export function useServerForm() {
  const [formData, setFormData] = useState<TelephonyServerFormData>(initialFormState);
  const [originalData, setOriginalData] = useState<TelephonyServerFormData>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (field: keyof TelephonyServerFormData, value: any) => {
    setFormData((prev) => {
      const updates: Partial<TelephonyServerFormData> = { [field]: value };
      
      // Auto-update eslHost when host is updated, IF eslHost is empty or matched the old host
      if (field === 'host') {
        if (!prev.eslHost || prev.eslHost === prev.host) {
          updates.eslHost = value;
        }
      }
      
      return {
        ...prev,
        ...updates,
      };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const resetForm = (newData?: TelephonyServerFormData) => {
    const data = newData || initialFormState;
    setFormData(data);
    setOriginalData(data);
    setErrors({});
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  return {
    formData,
    originalData,
    errors,
    setFormData,
    setOriginalData,
    setErrors,
    handleInputChange,
    resetForm,
    hasChanges,
  };
}

